import { ScatterplotLayer, TextLayer } from '@deck.gl/layers';
import {CollisionFilterExtension, DataFilterExtension} from '@deck.gl/extensions';
import CollisionFilterExtensionFix from '../utils/collision-filter-fix';
import { depthToColor } from '../utils/layer_style';
// import { mostRecentDepth, insideEventTimeRange } from '../utils/layer_style';
import { useOGCFunction, type GeoJsonFeature, useClosestTimeDeltas, useClosestTime } from '../utils/ogc';
import { useMemo } from 'react';
import type { PickingInfo } from '@deck.gl/core';
import useCurrentTimeFilter from '../utils/filtering';
import { DictionaryEncodingExtension } from '../utils/DictionaryEncodingExtension';
import { CreateMathExtensionSubclass, MathExtension } from '../utils/MathExtension';
import { TemporalFilterExtension } from '../utils/TemporalFilterExtension';
import { OGC_FUNCTIONS } from '../config';


type EventDepthDataFeatureProperties = {
  deployment_id: string;
  time: Date;
  depth_inches: number;
  label: string;
};

export type EventDepthDataFeature = GeoJsonFeature & {
  properties: EventDepthDataFeatureProperties;
};


export const transformEventWithDepthData = (d: GeoJsonFeature) => {
    d.id = d.properties.deployment_id;
    d.properties.time = new Date(d.properties.time);
    // d.properties.label = d.properties.label || '';
    return d as EventDepthDataFeature;
}

export const useEventDepthData = (timeRange: [Date, Date, boolean], params: Record<string, any>={}) => {
  const data = useOGCFunction(
    // 'postgisftw.event_depth_data', 
    // 'postgisftw.tentative_event_depth_data',
    OGC_FUNCTIONS.EVENT_DEPTH_DATA,
    { from_time: timeRange[0]?.toISOString(), to_time: timeRange[1]?.toISOString(), limit: 100000, ...params });
  data.features = useMemo(() => data.features?.map(transformEventWithDepthData), [data.features]);
  return data as { features: EventDepthDataFeature[] | undefined; loading: boolean; error: any };
};



const COLORS = {
  sensorVisible: [0, 155, 104],
  sensorHidden: [255, 0, 0],
  sensorOffline: [128, 128, 128],
  sensorRetired: [200, 200, 200],
  depthNone: [0, 155, 104],
  depthMinimal: [0, 204, 255],
  depthMinor: [255, 162, 68],
  depthModerate: [254, 77, 76],
  depthMajor: [215, 77, 254],
}

const SEVERITY_COLORMAP = [
  // [0, 0, 0, 0],
  // [0, 0, 0, 0],
  // [0, 0, 0, 0],
  [...COLORS.depthNone, 255],
  [...COLORS.depthMinimal, 255],
  [...COLORS.depthMinor, 255],
  [...COLORS.depthModerate, 255],
  [...COLORS.depthMajor, 255],
  //   [0, 0, 0, 255],
  // [255, 0, 0, 255],
  // [0, 255, 0, 255],
  // [0, 0, 255, 255],
  // [255, 255, 0, 255],
]//.map(c => c.map(v => v / 255)); // normalize to 0..1

const getSeverityIndex = (depth: number) => {
  return (
    depth <= 0 ? 0 :
    depth < 4 ? 1 :
    depth < 12 ? 2 :
    depth < 24 ? 3 :
    4
  );
}

// const CustomMathExtension = CreateMathExtensionSubclass({
//   name: 'depthdata',
//   attrs: {
//     categoryIndex: {
//       type: 'float',
//     },
//   },
//   uniforms: {
//     // fillColor: {
//     //   type: 'vec4',
//     //   utype: 'vec4<f32>',
//     //   value: SEVERITY_COLORMAP,
//     // },
//   },
//   inject: {
//     'fs:#decl': `
//     vec3 depthToColorSmooth(float d) {
//       vec3 c0 = vec3(0.0, 155.0, 104.0);  // 0
//       vec3 c1 = vec3(0.0, 204.0, 255.0);  // <4
//       vec3 c2 = vec3(255.0, 162.0, 68.0); // <12
//       vec3 c3 = vec3(254.0, 77.0, 76.0);  // <24
//       vec3 c4 = vec3(215.0, 77.0, 254.0);

//       if (d < 4.0)  return mix(c0, c1, smoothstep(0.0, 4.0, d));
//       if (d < 12.0) return mix(c1, c2, smoothstep(4.0, 12.0, d));
//       if (d < 24.0) return mix(c2, c3, smoothstep(12.0, 24.0, d));
//       return mix(c3, c4, smoothstep(24.0, 40.0, d)); // extend last ramp
//     }`,
//     'fs:DECKGL_FILTER_COLOR': `
//     vec4 paletteColor = depthdata.fillColor[int(vInstanceCategoryIndex)];
//     paletteColor /= 255.0;
//     color = vec4(paletteColor.rgb, color.a);
//     `.trim(),
//   },
//   // vs: '1/0',
// });
const CustomMathExtension = CreateMathExtensionSubclass({
  name: 'depthdata',
  attrs: {
    // categoryIndex: {
    //   type: 'float',
    // },
    depth: {
      type: 'float',
    },
    // currentDepth: {
    //   type: 'float',
    //   // stepMode: 'instance',
    // },
  },
  uniforms: {
    // fillColor: {
    //   type: 'vec4',
    //   utype: 'vec4<f32>',
    //   value: SEVERITY_COLORMAP,
    // },
  },
  inject: {
    'fs:#decl': `
    vec3 depthToColorSharp(float depth) {
      if (depth <= 0.0) {
        return vec3(0.0, 155.0, 104.0);
      }
      if (depth < 4.0) {
        return vec3(0.0, 204.0, 255.0);
      }
      if (depth < 12.0) {
        return vec3(255.0, 162.0, 68.0);
      }
      if (depth < 24.0) {
        return vec3(254.0, 77.0, 76.0);
      }
      return vec3(215.0, 77.0, 254.0);
    }
    vec4 depthToColorSmooth(float d) {
      // vec3 c0 = vec3(0.0, 155.0, 104.0, 0.0);  // 0
      vec4 c0 = vec4(150.0, 150.0, 150.0, 0.0);  // 0
      vec4 c11 = vec4(0.0, 204.0, 255.0, 1.0);  // <4
      vec4 c1 = vec4(0.0, 204.0, 255.0, 1.0);  // <4
      vec4 c2 = vec4(255.0, 162.0, 68.0, 1.0); // <12
      vec4 c3 = vec4(254.0, 77.0, 76.0, 1.0);  // <24
      vec4 c4 = vec4(215.0, 77.0, 254.0, 1.0);

      if (d < 1.0)  return mix(c0, c11, smoothstep(0.0, 1.0, d));
      if (d < 4.0)  return mix(c11, c1, smoothstep(0.0, 4.0, d));
      if (d < 12.0) return mix(c1, c2, smoothstep(4.0, 12.0, d));
      if (d < 24.0) return mix(c2, c3, smoothstep(12.0, 24.0, d));
      return mix(c3, c4, smoothstep(24.0, 40.0, d)); // extend last ramp
    }
    `,
    'fs:DECKGL_FILTER_COLOR': `
    float depth = vInstanceDepth;

    float alpha = smoothstep(0.0, 1.0, depth);
    // vec4 paletteColor = depthToColorSmooth(depth);
    vec4 paletteColor = vec4(depthToColorSharp(depth), 1.0);
    if (alpha < 0.005) discard;
    paletteColor /= 255.0;
    color = vec4(paletteColor.rgb, min(color.a, alpha));
    `.trim(),
  },
  // vs: '1/0',
});

export default function useEventDepthDataLayer(features: EventDepthDataFeature[], { 
    currentTime, 
    timeRange, 
    onClick 
  }: { currentTime?: Date; timeRange?: [Date, Date]; onClick?: (info: PickingInfo<EventDepthDataFeature>) => void }) {
  // const [start, end] = timeRange || [];
  // let { features } = useEventDepthData([start, end]);
  features = useMemo(() => features?.sort((a, b) => (a.properties.depth_inches - b.properties.depth_inches)), [features]);
  // console.log(features)

  const t0 = currentTime?.getTime() || 0;
  const closestTime = useClosestTime(features, currentTime ? new Date(currentTime.getTime() + 4 * 3600 * 1000) : undefined);
  const flags = useCurrentTimeFilter(features, currentTime);

  // const deploymentIds = useMemo(
  //   () => (!features ? [] : Array.from(new Set(features.map(f => f.properties.deployment_id)))),
  //   [features]
  // );

  // const deploymentIndexLookup = useMemo(() => {
  //   const map = new Map<string, number>();
  //   deploymentIds.forEach((id, index) => {
  //     map.set(id, index);
  //   });
  //   return map;
  // }, [deploymentIds]);

  // const timestampTable = useMemo(
  //   () => deploymentIds.map(id => closestTime[id]?.t ?? 0),
  //   [deploymentIds, closestTime]
  // );
  // console.log('timestampTable', timestampTable);

  return !features ? [] : [
    new ScatterplotLayer({
      id: 'event_with_depth_data-scatter',
      data: features,
      
      pickable: true,
      radiusMinPixels: 4,
      radiusUnits: 'pixels',
      stroked: true,
      filled: true,
      // getLineColor: [255, 255, 255, 200],
      getLineColor: [200, 200, 240, 200],

      getLineWidth: 0,
      lineWidthMinPixels: 0,
      minZoom: 8,
      maxZoom: 14,

      getPosition: (f: GeoJsonFeature) => [...f.geometry.coordinates, f.properties.depth_inches * 25.4 / 1000],
      // getFillColor: (f: GeoJsonFeature) => depthToColor(f.properties.depth_inches),
      getFillColor: [0, 0, 0, 255],
      getCategoryIndex: (f: EventDepthDataFeature) => {
        // console.log('depth', f.properties.depth_inches, '->', getSeverityIndex(f.properties.depth_inches));
        return getSeverityIndex(f.properties.depth_inches);
      }, // (0..3)
      // getDeploymentIndex: (f: EventDepthDataFeature) => deploymentIndexLookup.get(f.properties.deployment_id) ?? 0,
      // getTimestamp: (f: EventDepthDataFeature) => f.properties.time.getTime(),


      getRadius: (f: GeoJsonFeature) => {
        const depth = f.properties.depth_inches;
        return Math.round(depth ? 12 + 6 * depth / 24 : 4);
      },
      onClick,
      collisionTestProps: {radiusScale: 0.01, radiusUnits: 'meters'},
      getFilterValue: (f: GeoJsonFeature) => flags[f.__idx] ? 1 : -1,
      filterRange: [1, 1],
      // Feed temporal table into TemporalFilterExtension so it updates with currentTime
      // deploymentTimestampTable: timestampTable,
      getDepth: (f: EventDepthDataFeature) => f.properties.depth_inches,
      extensions: [
        // new CollisionFilterExtension(),
        new DataFilterExtension({ filterSize: 1 }),


        // new DictionaryEncodingExtension({
        //   palettes: {
        //     fillColor: SEVERITY_COLORMAP,
        //   },
        // }),


        new CustomMathExtension('depthdata', {}),



        // new TemporalFilterExtension({
        //   timestampTable,
        // }),
        
      ],

      updateTriggers: {
        // getFillColor: [currentTime],
        // getRadius: [currentTime],
        // getText: [currentTime],
        // getPosition: [currentTime],
        // getCollisionPriority: [t0],
        getFilterValue: [flags],
        // getCategoryIndex: [SEVERITY_COLORMAP],
        // deploymentTimestampTable: [timestampTable],
      },
      parameters: { depthTest: false },
    }),

    new TextLayer({
      id: 'events-label-focus',
      data: features,
      getPosition: (f: GeoJsonFeature): [number, number, number] => [f.geometry.coordinates[0], f.geometry.coordinates[1], f.properties.depth_inches * 25.4 / 1000 + 2],
      getText: (f: GeoJsonFeature): string => {
        const depth = f.properties.depth_inches;
        // return depth ? `${(Math.round(depth*10)/10).toFixed(1)}` : '';
        return depth ? `${(depth > 6 ? depth.toFixed(0) : depth.toFixed(1))}` : '';
      },
      getSize: (f: GeoJsonFeature): number => {
        const depth = f.properties.depth_inches;
        return depth ? (depth > 6 ? 16 : 12) : 0;
      },

      getAlignmentBaseline: 'center',
      getAnchor: 'middle',
      billboard: true,
      getColor: [255, 255, 255, 255],
      // getSize: 12,
      fontWeight: 900,
      fontFamily: "Verdana, Roboto, Helvetica Neue, Helvetica, sans-serif",
      parameters: { depthTest: false },
      polygonOffset: 1,
      collisionGroup: 'depth-labels',
      collisionTestProps: { sizeScale: 2 },

      getCollisionPriority: function(f: GeoJsonFeature) {
        return Math.round(f.properties.depth_inches) * 25.4 - 1000;
      },
      getFilterValue: (f: GeoJsonFeature) => flags[f.__idx] ? 1 : -1,
      filterRange: [1, 1],
      extensions: [
        new CollisionFilterExtensionFix(),
        new DataFilterExtension({ filterSize: 1 })
      ],
      // getPolygonOffset: () => [1, 1],
      // collisionTestProps: { sizeScale: 3 },
        updateTriggers: {
          // getText: [currentTime],
          // getPosition: [currentTime],
          // getCollisionPriority: [currentTime],
          getFilterValue: [flags],
        },
    })
  ]
}
